// ColumnEnergyStateChart.jsx
import { Card } from '@mui/material';
import {
  ThemeProvider,
  createTheme,
  useTheme,
  alpha,
} from '@mui/material/styles';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useMemo } from 'react';

const formatDateTime = (iso) => {
  if (!iso || iso === 'NaT') return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('da-DK', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatNumber = (n, digits = 2) =>
  Number.isFinite(n)
    ? new Intl.NumberFormat('da-DK', { maximumFractionDigits: digits }).format(
        n,
      )
    : '—';

const stateColor = ({ is_paused, is_control, is_baseline }) => {
  if (is_paused) return '#9E9E9E'; // gray
  if (is_control) return '#96b100'; // green
  if (is_baseline) return '#C62828'; // red
  return '#90A4AE'; // fallback
};

const stateLabel = ({ is_paused, is_control, is_baseline }) => {
  if (is_paused) return 'Inaktiv/Pause';
  if (is_control) return 'Aktiv styring';
  if (is_baseline) return 'Baseline (intet indgreb)';
  return 'Unknown';
};

export default function ColumnEnergyStateChart({
  data = [],
  title = 'Usage',
  yAxisTitle = 'Value',
  height = 360,
  mode, // 'light' | 'dark' (optional). If omitted, inherit parent theme mode.
}) {
  // ---- THEME ----
  const parentTheme = useTheme();
  const theme = useMemo(
    () => createTheme({ palette: { mode: mode ?? parentTheme.palette.mode } }),
    [mode, parentTheme.palette.mode],
  );
  const isDark = theme.palette.mode === 'dark';

  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const bgPaper = theme.palette.background.paper;
  const gridColor = alpha(textPrimary, isDark ? 0.25 : 0.12);
  const axisLine = alpha(textPrimary, isDark ? 0.35 : 0.18);
  const tooltipBg = alpha(bgPaper, 0.98);
  const tooltipBorder = alpha(textPrimary, isDark ? 0.5 : 0.2);

  // Build categories + points
  const { categories, points } = useMemo(() => {
    const cats = [];
    const pts = (Array.isArray(data) ? data : []).map((d, i) => {
      const color = stateColor(d);
      const label = d.date || `#${i + 1}`;
      cats.push(label);
      return {
        y: typeof d.value === 'number' ? d.value : Number(d.value ?? 0),
        color,
        custom: {
          date: d.date,
          start: d.start,
          end: d.end,
          is_paused: !!d.is_paused,
          is_control: !!d.is_control,
          is_baseline: !!d.is_baseline,
        },
      };
    });
    return { categories: cats, points: pts };
  }, [data]);

  const options = useMemo(
    () => ({
      chart: {
        type: 'column',
        height,
        backgroundColor: bgPaper,
        style: { fontFamily: theme.typography.fontFamily },
      },
      title: {
        text: title,
        style: { color: textPrimary, fontWeight: 600, fontSize: '14px' },
      },
      credits: { enabled: false },
      legend: { enabled: false },

      xAxis: {
        categories,
        title: { text: null },
        tickLength: 0,
        lineColor: axisLine,
        tickColor: axisLine,
        labels: { style: { color: textSecondary } },
      },

      yAxis: {
        title: { text: yAxisTitle, style: { color: textSecondary } },
        allowDecimals: false,
        gridLineColor: gridColor,
        lineColor: axisLine,
        tickColor: axisLine,
        labels: { style: { color: textSecondary } },
      },

      plotOptions: {
        column: {
          borderWidth: 0,
          pointPadding: 0.1,
          groupPadding: 0.08,
          borderRadius: 3,
          dataLabels: { enabled: false },
          states: { hover: { enabled: true } },
        },
        series: { animation: { duration: 250 } },
      },

      tooltip: {
        useHTML: true,
        backgroundColor: tooltipBg,
        borderColor: tooltipBorder,
        style: { color: textPrimary },
        formatter: function () {
          const p = this.point;
          const s = p.custom || {};
          const label = stateLabel(s);
          const start = formatDateTime(s.start);
          const end = formatDateTime(s.end);
          const dotColor = this.point?.color || '#90A4AE';

          return `
             <div style="min-width:320px">
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px">
                <span style="
                  display:inline-block;
                  width:10px;height:10px;border-radius:50%;
                  background:${dotColor};
                  flex:0 0 auto;
                "></span>
                <b>${label}</b>
              </div>
              <div>Målt forbrug: <b>${formatNumber(this.y)} kWh</b></div>
              <hr style="border:none;border-top:1px solid ${alpha(textPrimary, 0.15)};margin:6px 0"/>
              <div>Periode:</div>
              <div><small>${start} → ${end}</small></div>
            </div>
          `;
        },
      },

      series: [
        {
          type: 'column',
          data: points,
          name: 'Value',
        },
      ],
    }),
    [
      categories,
      points,
      title,
      yAxisTitle,
      height,
      // theme-driven deps:
      bgPaper,
      textPrimary,
      textSecondary,
      gridColor,
      axisLine,
      tooltipBg,
      tooltipBorder,
      theme.typography.fontFamily,
    ],
  );

  return (
    <ThemeProvider theme={theme}>
      <Card
        variant="outlined"
        sx={{
          p: 2,
          bgcolor: bgPaper,
          borderColor: theme.palette.divider,
        }}
      >
        <HighchartsReact highcharts={Highcharts} options={options} />
      </Card>
    </ThemeProvider>
  );
}
